import * as Sentry from '@sentry/react';
const parseRate = (value, fallback) => {
    if (typeof value === 'number') {
        return value;
    }
    if (typeof value === 'string') {
        const parsed = Number.parseFloat(value);
        if (Number.isFinite(parsed)) {
            return parsed;
        }
    }
    return fallback;
};
const resolveBrowserEnv = () => {
    try {
        return import.meta?.env ?? undefined;
    }
    catch {
        return undefined;
    }
};
const resolveNodeEnv = () => {
    if (typeof process === 'undefined') {
        return undefined;
    }
    return process.env;
};
const resolveTracePropagationTargets = (browserEnv, nodeEnv) => {
    const targets = [/^\/(?!\/)/, /^https?:\/\/localhost(?::\d+)?/, /^https?:\/\/127\.0\.0\.1(?::\d+)?/];
    const appendUrlOrigin = (value) => {
        if (!value) {
            return;
        }
        try {
            const parsed = new URL(value);
            targets.push(parsed.origin);
        }
        catch {
            // Ignore invalid URLs; Sentry will fall back to defaults.
        }
    };
    appendUrlOrigin(browserEnv?.VITE_API_URL ?? nodeEnv?.VITE_API_URL);
    appendUrlOrigin(browserEnv?.VITE_MEDIA_URL ?? nodeEnv?.VITE_MEDIA_URL);
    return targets;
};
let webInitialized = false;
export const isExpectedBrowserAbortError = (value) => {
    if (!value || typeof value !== 'object') {
        return false;
    }
    const name = 'name' in value && typeof value.name === 'string' ? value.name.toLowerCase() : '';
    const message = 'message' in value && typeof value.message === 'string' ? value.message.toLowerCase() : '';
    return (name === 'aborterror' ||
        message === 'aborterror' ||
        message.includes('aborterror:') ||
        message.includes('the operation was aborted') ||
        message.includes('operation was aborted'));
};
export const initWebTelemetry = (options = {}) => {
    if (webInitialized) {
        return;
    }
    const browserEnv = resolveBrowserEnv();
    const nodeEnv = resolveNodeEnv();
    const isDev = Boolean(browserEnv?.DEV ?? nodeEnv?.NODE_ENV === 'development');
    const debugContext = {
        hasBrowserEnv: Boolean(browserEnv),
        hasNodeEnv: Boolean(nodeEnv),
        isDev
    };
    if (typeof console !== 'undefined' && isDev) {
        console.debug('[observability] initWebTelemetry invoked', debugContext);
    }
    if (typeof window !== 'undefined' && isDev) {
        window.__athwabTelemetryDebug = {
            ...debugContext,
            dsn: undefined,
            initialized: false
        };
    }
    const dsn = options.dsn ??
        browserEnv?.VITE_SENTRY_DSN ??
        browserEnv?.SENTRY_DSN ??
        nodeEnv?.SENTRY_DSN;
    if (!dsn) {
        if (typeof console !== 'undefined' && isDev) {
            console.warn('[observability] Sentry DSN missing; browser telemetry disabled');
        }
        if (typeof window !== 'undefined' && isDev) {
            window.__athwabTelemetryDebug = {
                ...debugContext,
                dsn: null,
                initialized: false
            };
        }
        return;
    }
    const environment = options.environment ??
        browserEnv?.VITE_SENTRY_ENVIRONMENT ??
        browserEnv?.MODE ??
        nodeEnv?.SENTRY_ENVIRONMENT ??
        'development';
    const release = options.release ??
        browserEnv?.VITE_APP_RELEASE ??
        browserEnv?.VITE_GIT_SHA ??
        nodeEnv?.SENTRY_RELEASE;
    const tracesSampleRate = parseRate(options.tracesSampleRate ?? browserEnv?.VITE_SENTRY_TRACES_SAMPLE_RATE ?? nodeEnv?.SENTRY_TRACES_SAMPLE_RATE, environment === 'production' ? 0.2 : 1.0);
    const replaysSessionSampleRate = parseRate(options.replaysSessionSampleRate ?? browserEnv?.VITE_SENTRY_REPLAYS_SESSION_SAMPLE_RATE ?? nodeEnv?.SENTRY_REPLAYS_SESSION_SAMPLE_RATE, 0);
    const replaysOnErrorSampleRate = parseRate(options.replaysOnErrorSampleRate ?? browserEnv?.VITE_SENTRY_REPLAYS_ON_ERROR_SAMPLE_RATE ?? nodeEnv?.SENTRY_REPLAYS_ON_ERROR_SAMPLE_RATE, 1);
    const tracePropagationTargets = resolveTracePropagationTargets(browserEnv, nodeEnv);
    try {
        Sentry.init({
            dsn,
            environment,
            release,
            integrations: [
                Sentry.browserTracingIntegration(),
                Sentry.replayIntegration({
                    maskAllText: true,
                    blockAllMedia: true
                })
            ],
            tracesSampleRate,
            replaysSessionSampleRate,
            replaysOnErrorSampleRate,
            tracePropagationTargets,
            beforeSend(event, hint) {
                if (isExpectedBrowserAbortError(hint.originalException)) {
                    return null;
                }
                const exceptionValues = event.exception?.values ?? [];
                const hasAbortException = exceptionValues.some((exception) => isExpectedBrowserAbortError({
                    name: exception.type,
                    message: exception.value,
                }));
                return hasAbortException ? null : event;
            }
        });
    }
    catch (error) {
        if (typeof console !== 'undefined') {
            console.error('[observability] Failed to initialise Sentry web SDK', error);
        }
        if (typeof window !== 'undefined' && isDev) {
            window.__athwabTelemetryDebug = {
                ...debugContext,
                dsn,
                initialized: false,
                environment,
                release,
                error: error?.message ?? error
            };
        }
        return;
    }
    Sentry.setTag('service.name', 'web');
    if (release) {
        Sentry.setTag('service.version', release);
    }
    if (typeof window !== 'undefined' && isDev) {
        window.__athwabTelemetryDebug = {
            ...debugContext,
            dsn,
            initialized: true,
            environment,
            release
        };
    }
    if (typeof window !== 'undefined' && isDev) {
        window.__athwabCaptureException = (error) => {
            const value = error instanceof Error ? error : new Error('web smoke test');
            Sentry.captureException(value);
        };
    }
    webInitialized = true;
};
//# sourceMappingURL=web.js.map